import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, Edit as EditIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { formatCurrency } from '../utils/formatters';

const RANGE_OPTIONS = [
  { key: '30d', label: 'Last 30 days' },
  { key: '60d', label: 'Last 60 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'ytd', label: 'Year to date' },
  { key: '1y', label: 'Last year' },
  { key: '2y', label: 'Last 2 years' },
  { key: 'custom', label: 'Custom range…' },
];
const RANGE_DAYS = { '30d': 30, '60d': 60, '90d': 90, '1y': 365, '2y': 730 };

const humanizeCategory = (c) =>
  String(c || 'Uncategorized')
    .toLowerCase()
    .split('_')
    .map(w => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');

// Normalize a category/subcategory name so typed overrides dedupe against
// Plaid enum values ("Income > Wages" ≡ INCOME|WAGES).
const norm = (s) =>
  String(s || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const normName = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

// "Primary > Sub" overrides come back as { primary, sub }
const splitOverride = (s) => {
  const parts = String(s || '').split('>').map(x => x.trim()).filter(Boolean);
  return { primary: parts[0] || null, sub: parts[1] || null };
};

// Strip the PRIMARY_ prefix off Plaid's detailed category for sub display.
const plaidSub = (primary, detailed) => {
  if (!detailed || detailed === primary) return null;
  const p = String(primary || '') + '_';
  return String(detailed).startsWith(p) ? String(detailed).slice(p.length) : String(detailed);
};

const leafKeyOf = (c) => `${norm(c.primary)}|${norm(c.sub || '')}`;
const leafLabelOf = (key) => {
  const [p, s] = String(key).split('|');
  return s ? `${humanizeCategory(p)} › ${humanizeCategory(s)}` : humanizeCategory(p);
};

function Transactions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [fetchErrors, setFetchErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [range, setRange] = useState('90d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [hidePending, setHidePending] = useState(false);
  const [hideTransfers, setHideTransfers] = useState(true);
  const [showAllCats, setShowAllCats] = useState(false);

  const [manageCats, setManageCats] = useState(false);
  const [renaming, setRenaming] = useState(null);   // { primary, sub|null }
  const [renameVal, setRenameVal] = useState('');
  const [newCatVal, setNewCatVal] = useState('');
  const [addingSubFor, setAddingSubFor] = useState(null); // norm(primary)
  const [newSubVal, setNewSubVal] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set()); // rowIds
  const [bulkDialog, setBulkDialog] = useState(null);          // { primary, sub, applyFuture }
  const tableRef = useRef(null);

  const [catDialog, setCatDialog] = useState(null); // { txn, primary, sub, applyFuture }
  const [catRules, setCatRules] = useState([]);     // [{ match, primary, sub, fromDate }]
  const [customCats, setCustomCats] = useState([]); // [{ primary, subs: [] }]

  // Effective category: explicit row override > matching merchant rule > Plaid.
  const effCat = (t) => {
    if (t.categoryOverride) {
      const o = splitOverride(t.categoryOverride);
      return { primary: o.primary || 'Uncategorized', sub: o.sub, source: 'override' };
    }
    const nm = normName(t.name);
    const rule = catRules.find(r => r.match === nm && String(t.date || '') >= String(r.fromDate || ''));
    if (rule) return { primary: rule.primary, sub: rule.sub || null, source: 'rule' };
    return {
      primary: t.categoryPrimary || 'Uncategorized',
      sub: plaidSub(t.categoryPrimary, t.categoryDetailed),
      source: 'plaid',
    };
  };

  // Transfers (TRANSFER_IN/TRANSFER_OUT, credit card payments, or any custom
  // "Transfer*" category) aren't income or expenses — excluded from the summary.
  const isTransferCat = (t) => {
    const c = effCat(t);
    return norm(c.primary).startsWith('TRANSFER') || norm(c.sub || c.primary) === 'CREDIT_CARD_PAYMENT';
  };

  // Primaries that come from Plaid data itself (not editable in the manager).
  const plaidPrimaryNorms = useMemo(() => {
    const s = new Set();
    transactions.forEach(t => {
      const c = effCat(t);
      if (c.source === 'plaid') s.add(norm(c.primary));
    });
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, catRules]);

  // A leaf is user-managed iff it exists in customCats — except primaries that
  // Plaid itself produced, which can't be renamed away.
  const isCustomLeaf = (primary, sub) => {
    const np = norm(primary);
    const cc = customCats.find(x => norm(x.primary) === np);
    if (!cc) return false;
    if (!sub) return !plaidPrimaryNorms.has(np);
    return (cc.subs || []).some(s => norm(s) === norm(sub));
  };

  const registerCustomCat = (primary, sub) => {
    const np = norm(primary), ns = norm(sub || '');
    let cc = [...customCats];
    const ei = cc.findIndex(x => norm(x.primary) === np);
    let changed = false;
    if (ei < 0) {
      cc.push({ primary, subs: sub ? [sub] : [] });
      changed = true;
    } else if (sub && !(cc[ei].subs || []).some(s => norm(s) === ns)) {
      cc[ei] = { ...cc[ei], subs: [...(cc[ei].subs || []), sub] };
      changed = true;
    }
    if (changed) {
      setCustomCats(cc);
      putPref('bank_custom_categories', cc);
    }
  };

  const patchRowOverride = (rowId, category) =>
    apiRequest(`/api/plaid-api/bank-transactions/${rowId}`, {
      method: 'PATCH',
      body: JSON.stringify({ category }),
    });

  // PATCH category_override for a set of loaded rows: Map(rowId -> value|null)
  const bulkPatchOverrides = async (updates) => {
    if (!updates.size) return;
    const results = await Promise.all([...updates.entries()].map(([rowId, cat]) =>
      patchRowOverride(rowId, cat)
        .then(r => [rowId, r.data?.categoryOverride ?? null])
        .catch(() => [rowId, undefined])
    ));
    setTransactions(prev => prev.map(t => {
      const hit = results.find(([id]) => id === t.rowId);
      return hit && hit[1] !== undefined ? { ...t, categoryOverride: hit[1] } : t;
    }));
  };

  const commitRename = async () => {
    const r = renaming;
    const name = renameVal.trim();
    setRenaming(null);
    if (!r || !name) return;
    const np = norm(r.primary), ns = norm(r.sub || '');
    if (r.sub ? norm(name) === ns : norm(name) === np) return;
    // customCats
    const cc = customCats.map(x => ({ ...x, subs: [...(x.subs || [])] }));
    const ei = cc.findIndex(x => norm(x.primary) === np);
    if (ei >= 0) {
      if (r.sub) cc[ei].subs = cc[ei].subs.map(s => norm(s) === ns ? name : s);
      else cc[ei].primary = name;
      setCustomCats(cc);
      putPref('bank_custom_categories', cc);
    }
    // merchant rules referencing the old leaf
    const rules = catRules.map(rule => {
      if (r.sub) return norm(rule.primary) === np && norm(rule.sub || '') === ns ? { ...rule, sub: name } : rule;
      return norm(rule.primary) === np ? { ...rule, primary: name } : rule;
    });
    if (JSON.stringify(rules) !== JSON.stringify(catRules)) {
      setCatRules(rules);
      putPref('bank_category_rules', rules);
    }
    // per-row overrides on loaded transactions
    const updates = new Map();
    transactions.forEach(t => {
      const o = splitOverride(t.categoryOverride);
      if (!o.primary || !t.rowId) return;
      if (r.sub) {
        if (norm(o.primary) === np && norm(o.sub || '') === ns) updates.set(t.rowId, `${o.primary} > ${name}`);
      } else if (norm(o.primary) === np) {
        updates.set(t.rowId, o.sub ? `${name} > ${o.sub}` : name);
      }
    });
    try { await bulkPatchOverrides(updates); } catch { /* partial failure ok */ }
  };

  const deleteLeaf = async (primary, sub) => {
    const np = norm(primary), ns = norm(sub || '');
    const affected = transactions.filter(t => {
      const o = splitOverride(t.categoryOverride);
      if (!o.primary || norm(o.primary) !== np) return false;
      return sub ? norm(o.sub || '') === ns : true;
    });
    const label = sub ? `${humanizeCategory(primary)} › ${humanizeCategory(sub)}` : humanizeCategory(primary);
    const msg = affected.length
      ? `Remove "${label}"? ${affected.length} loaded transaction(s) using it will ${sub ? 'fall back to the parent category' : 'reset to automatic'}.`
      : `Remove "${label}"?`;
    if (!window.confirm(msg)) return;
    let cc = customCats.map(x => ({ ...x, subs: [...(x.subs || [])] }));
    if (sub) {
      cc = cc.map(x => norm(x.primary) === np ? { ...x, subs: (x.subs || []).filter(s => norm(s) !== ns) } : x);
    } else {
      cc = cc.filter(x => norm(x.primary) !== np);
    }
    setCustomCats(cc);
    putPref('bank_custom_categories', cc);
    const rules = catRules
      .map(rule => {
        if (norm(rule.primary) !== np) return rule;
        if (sub) return norm(rule.sub || '') === ns ? { ...rule, sub: null } : rule;
        return null;
      })
      .filter(Boolean);
    if (JSON.stringify(rules) !== JSON.stringify(catRules)) {
      setCatRules(rules);
      putPref('bank_category_rules', rules);
    }
    const updates = new Map();
    affected.forEach(t => {
      if (t.rowId) updates.set(t.rowId, sub ? (splitOverride(t.categoryOverride).primary || primary) : null);
    });
    try { await bulkPatchOverrides(updates); } catch { /* partial failure ok */ }
  };

  const toggleRowSelected = (rowId) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };

  const saveBulkCategory = async () => {
    const d = bulkDialog;
    if (!d) return;
    const primary = String(d.primary || '').trim();
    const sub = String(d.sub || '').trim();
    if (!primary) return;
    setBulkDialog(null);
    const overrideStr = sub ? `${primary} > ${sub}` : primary;
    const updates = new Map();
    const matches = new Set();
    transactions.forEach(t => {
      if (t.rowId && selectedRows.has(t.rowId)) {
        updates.set(t.rowId, overrideStr);
        if (t.name) matches.add(normName(t.name));
      }
    });
    try {
      await bulkPatchOverrides(updates);
    } catch (e) {
      setError('Some categories could not be saved');
    }
    registerCustomCat(primary, sub);
    if (d.applyFuture && matches.size) {
      const rules = [...catRules];
      const fromDate = new Date().toISOString().slice(0, 10);
      matches.forEach(m => {
        const i = rules.findIndex(r => r.match === m);
        const rule = { match: m, primary, sub: sub || null, fromDate };
        if (i >= 0) rules[i] = rule; else rules.push(rule);
      });
      setCatRules(rules);
      putPref('bank_category_rules', rules);
    }
    setSelectedRows(new Set());
  };

  const fetchTransactions = useCallback(async (refresh = false, background = false) => {
    const end = new Date();
    const endStr = end.toISOString().slice(0, 10);
    let startStr;
    if (range === 'custom') {
      if (!customStart || !customEnd) return;
      startStr = customStart;
    } else if (range === 'ytd') {
      startStr = `${end.getFullYear()}-01-01`;
    } else {
      const start = new Date();
      start.setDate(start.getDate() - (RANGE_DAYS[range] || 90));
      startStr = start.toISOString().slice(0, 10);
    }
    if (!background) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        accountId: 'all',
        startDate: startStr,
        endDate: range === 'custom' ? customEnd : endStr,
      });
      if (refresh) params.set('refresh', '1');
      const resp = await apiRequest(`/api/plaid-api/bank-transactions?${params}`);
      const data = resp.data || {};
      setTransactions(data.transactions || []);
      setAccounts(data.accounts || []);
      setFetchErrors(data.errors || []);
    } catch (e) {
      setError(e.message || 'Failed to load transactions');
    } finally {
      if (!background) setLoading(false);
    }
  }, [range, customStart, customEnd]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // The endpoint serves a cached table and only contacts Plaid on refresh=1.
  // Auto-refresh in the background at most once per 24h so the cache doesn't
  // silently go stale between visits — cached rows stay visible meanwhile.
  useEffect(() => {
    const KEY = 'bankTxns_lastAutoRefresh';
    const last = parseInt(localStorage.getItem(KEY) || '0', 10);
    if (Date.now() - last < 24 * 60 * 60 * 1000) return;
    localStorage.setItem(KEY, String(Date.now()));
    const t = setTimeout(() => fetchTransactions(true, true), 1500);
    return () => clearTimeout(t);
  }, [fetchTransactions]);

  // Load category rules + custom categories (user_preferences; silent if absent)
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          apiRequest('/api/preferences/bank_category_rules'),
          apiRequest('/api/preferences/bank_custom_categories'),
        ]);
        if (Array.isArray(r1.data?.value)) setCatRules(r1.data.value);
        if (Array.isArray(r2.data?.value)) setCustomCats(r2.data.value);
      } catch { /* preferences unavailable */ }
    })();
  }, []);

  const putPref = (key, value) =>
    apiRequest(`/api/preferences/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }).catch(() => {});

  // Taxonomy for dropdowns: observed categories ∪ custom ones, grouped primary → subs
  const taxonomy = useMemo(() => {
    const map = new Map(); // normP -> { primary, subs: Map(normS -> sub) }
    const add = (p, s) => {
      const np = norm(p);
      if (!np) return;
      if (!map.has(np)) map.set(np, { primary: p, subs: new Map() });
      const e = map.get(np);
      const ns = norm(s || '');
      if (!e.subs.has(ns)) e.subs.set(ns, s || '');
    };
    transactions.forEach(t => { const c = effCat(t); add(c.primary, c.sub); });
    customCats.forEach(cc => { add(cc.primary, ''); (cc.subs || []).forEach(s => add(cc.primary, s)); });
    return [...map.values()]
      .map(({ primary, subs }) => ({ primary, subs: [...subs.values()].sort() }))
      .sort((a, b) => a.primary.localeCompare(b.primary));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, catRules, customCats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return transactions.filter(t => {
      if (selectedAccounts.length && !selectedAccounts.includes(String(t.accountId))) return false;
      if (selectedCategories.length && !selectedCategories.includes(leafKeyOf(effCat(t)))) return false;
      if (hidePending && t.pending) return false;
      const label = humanizeCategory(effCat(t).sub || effCat(t).primary);
      if (q && !String(t.name || '').toLowerCase().includes(q)
          && !label.toLowerCase().includes(q)
          && !String(effCat(t).primary).toLowerCase().includes(q)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, selectedAccounts, selectedCategories, search, hidePending, catRules]);

  const summary = useMemo(() => {
    // Plaid sign convention: positive amount = money out.
    const exp = new Map(), inc = new Map();
    let out = 0, into = 0;
    filtered.forEach(t => {
      if (hideTransfers && isTransferCat(t)) return;
      const amt = Number(t.amount) || 0;
      const cat = leafKeyOf(effCat(t));
      if (amt >= 0) { out += amt; exp.set(cat, (exp.get(cat) || 0) + amt); }
      else { into += -amt; inc.set(cat, (inc.get(cat) || 0) + (-amt)); }
    });
    const toList = (m) => [...m.entries()].sort((a, b) => b[1] - a[1]);
    return { out, into, net: into - out, expenses: toList(exp), income: toList(inc) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, hideTransfers]);

  const primaryOptions = useMemo(() => taxonomy.map(t => humanizeCategory(t.primary)), [taxonomy]);
  const subOptionsFor = (primary) => {
    const entry = taxonomy.find(e => norm(e.primary) === norm(primary));
    return entry ? entry.subs.filter(Boolean).map(humanizeCategory) : [];
  };

  const openCatDialog = (t) => {
    if (!t.rowId) return;
    const c = effCat(t);
    setCatDialog({
      txn: t,
      primary: humanizeCategory(c.primary),
      sub: c.sub ? humanizeCategory(c.sub) : '',
      applyFuture: false,
    });
  };

  const saveCatDialog = async () => {
    const d = catDialog;
    if (!d) return;
    const t = d.txn;
    const primary = String(d.primary || '').trim();
    const sub = String(d.sub || '').trim();
    if (!primary) return;
    setCatDialog(null);
    const overrideStr = sub ? `${primary} > ${sub}` : primary;
    try {
      if (t.rowId && overrideStr !== (t.categoryOverride || '')) {
        const resp = await apiRequest(`/api/plaid-api/bank-transactions/${t.rowId}`, {
          method: 'PATCH',
          body: JSON.stringify({ category: overrideStr }),
        });
        const override = resp.data?.categoryOverride ?? overrideStr;
        setTransactions(prev => prev.map(x => x.rowId === t.rowId ? { ...x, categoryOverride: override } : x));
      }
      // Register any newly-typed category/subcategory so it appears as an
      // option next time.
      registerCustomCat(primary, sub);
      // Optional: persist a merchant-match rule for future transactions.
      if (d.applyFuture && t.name) {
        const match = normName(t.name);
        const rules = [
          ...catRules.filter(r => r.match !== match),
          { match, primary, sub: sub || null, fromDate: new Date().toISOString().slice(0, 10) },
        ];
        setCatRules(rules);
        putPref('bank_category_rules', rules);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to update category');
    }
  };

  const resetCategory = async () => {
    const d = catDialog;
    if (!d || !d.txn.rowId) { setCatDialog(null); return; }
    const t = d.txn;
    setCatDialog(null);
    try {
      await apiRequest(`/api/plaid-api/bank-transactions/${t.rowId}`, {
        method: 'PATCH',
        body: JSON.stringify({ category: null }),
      });
      setTransactions(prev => prev.map(x => x.rowId === t.rowId ? { ...x, categoryOverride: null } : x));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to reset category');
    }
  };

  const multiSelectProps = {
    multiple: true,
    renderValue: (sel) => sel.length === 0 ? 'All' : `${sel.length} selected`,
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Income / Expense summary — same card style as the Dashboard stat boxes */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: 2 }}>
        {[
          { label: 'Income', value: `+${formatCurrency(summary.into)}`, color: 'success.main' },
          { label: 'Expenses', value: `-${formatCurrency(summary.out)}`, color: 'error.main' },
          {
            label: 'Net Income',
            value: `${summary.net >= 0 ? '+' : '-'}${formatCurrency(Math.abs(summary.net))}`,
            color: summary.net >= 0 ? 'success.main' : 'error.main',
          },
        ].map(({ label, value, color }) => (
          <Box key={label} sx={{ flex: '1 1 0', minWidth: { xs: '45%', sm: 0 } }}>
            <Card>
              <CardContent sx={{ p: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
                <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: { xs: '0.65rem', sm: undefined } }}>
                  {label}
                </Typography>
                <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }} color={color}>
                  {value}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>

        {(summary.expenses.length > 0 || summary.income.length > 0) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              {[
                { title: 'Expenses by category', list: summary.expenses, total: summary.out, color: 'error.main' },
                { title: 'Income by category', list: summary.income, total: summary.into, color: 'success.main' },
              ].map(({ title, list, total, color }) => list.length > 0 && (
                <Box key={title} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {title}
                  </Typography>
                  {(showAllCats ? list : list.slice(0, 6)).map(([cat, amt]) => (
                    <Stack
                      key={cat}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      title="Click to show transactions for this category"
                      onClick={() => {
                        setSelectedCategories(prev => (prev.length === 1 && prev[0] === cat) ? [] : [cat]);
                        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      sx={{ py: 0.25, px: 0.5, mx: -0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Typography variant="body2" sx={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
                        {leafLabelOf(cat)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={total > 0 ? Math.min(100, (amt / total) * 100) : 0}
                        sx={{ flex: 1, height: 6, borderRadius: 1, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color } }}
                      />
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 90, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatCurrency(amt)}
                      </Typography>
                    </Stack>
                  ))}
                  {list.length > 6 && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setShowAllCats(v => !v)}
                    >
                      {showAllCats ? 'Show less' : `+${list.length - 6} more categor${list.length - 6 === 1 ? 'y' : 'ies'}`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={range}
              label="Period"
              onChange={e => setRange(e.target.value)}
            >
              {RANGE_OPTIONS.map(r => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          {range === 'custom' && (
            <>
              <TextField
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                sx={{ width: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                sx={{ width: 150 }}
              />
            </>
          )}

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Account</InputLabel>
            <Select
              {...multiSelectProps}
              value={selectedAccounts}
              onChange={e => setSelectedAccounts(e.target.value)}
              input={<OutlinedInput label="Account" />}
            >
              {accounts.map(a => (
                <MenuItem key={a.id} value={String(a.id)}>
                  <Checkbox checked={selectedAccounts.includes(String(a.id))} size="small" />
                  <ListItemText primary={a.name} secondary={a.category} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Category</InputLabel>
            <Select
              {...multiSelectProps}
              value={selectedCategories}
              onChange={e => setSelectedCategories(e.target.value)}
              input={<OutlinedInput label="Category" />}
            >
              {taxonomy.map(({ primary, subs }) => [
                <ListSubheader
                  key={`h-${norm(primary)}`}
                  sx={{ lineHeight: '26px', fontWeight: 700, fontSize: '0.72rem', bgcolor: 'background.paper' }}
                >
                  {humanizeCategory(primary)}
                </ListSubheader>,
                ...subs.map(sub => {
                  const val = `${norm(primary)}|${norm(sub)}`;
                  return (
                    <MenuItem key={val} value={val} dense sx={{ pl: sub ? 3.5 : 1.5 }}>
                      <Checkbox checked={selectedCategories.includes(val)} size="small" sx={{ py: 0.25 }} />
                      <ListItemText
                        primary={sub ? humanizeCategory(sub) : humanizeCategory(primary)}
                        primaryTypographyProps={{ fontSize: sub ? '0.78rem' : '0.85rem', color: sub ? 'text.secondary' : 'text.primary' }}
                      />
                    </MenuItem>
                  );
                }),
              ])}
              <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setManageCats(true)}>
                  Manage categories…
                </Button>
              </Box>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search description or category"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />

          <Chip
            label="Hide pending"
            size="small"
            variant={hidePending ? 'filled' : 'outlined'}
            color={hidePending ? 'primary' : 'default'}
            onClick={() => setHidePending(v => !v)}
          />

          <Tooltip title="Exclude TRANSFER categories from the income/expense totals">
            <Chip
              label="Exclude transfers"
              size="small"
              variant={hideTransfers ? 'filled' : 'outlined'}
              color={hideTransfers ? 'primary' : 'default'}
              onClick={() => setHideTransfers(v => !v)}
            />
          </Tooltip>

          <Tooltip title="Refresh from Plaid (otherwise serves cached data)">
            <span>
              <IconButton size="small" onClick={() => fetchTransactions(true)} disabled={loading} sx={{ ml: 'auto' }}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {fetchErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {fetchErrors.map((e, i) => (
            <div key={i}>{e.accounts.join(', ')}: {e.error}</div>
          ))}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`${filtered.length} transactions`} variant="outlined" />
        <Chip size="small" label={`Out: -${formatCurrency(summary.out)}`}
          sx={{ color: 'error.main' }} variant="outlined" />
        <Chip size="small" label={`In: +${formatCurrency(summary.into)}`}
          sx={{ color: 'success.main' }} variant="outlined" />
        <Chip size="small" label={`Net: ${summary.net >= 0 ? '+' : '-'}${formatCurrency(Math.abs(summary.net))}`}
          variant="outlined" />
      </Stack>

      {selectedRows.size > 0 && (
        <Paper sx={{ px: 2, py: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600}>
            {selectedRows.size} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => setBulkDialog({ primary: '', sub: '', applyFuture: false })}
          >
            Apply category
          </Button>
          <Button size="small" onClick={() => setSelectedRows(new Set())}>
            Clear
          </Button>
        </Paper>
      )}

      <TableContainer component={Paper} ref={tableRef}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                {(() => {
                  const ids = filtered.filter(t => t.rowId).map(t => t.rowId);
                  const allSel = ids.length > 0 && ids.every(id => selectedRows.has(id));
                  const someSel = ids.some(id => selectedRows.has(id));
                  return (
                    <Checkbox
                      size="small"
                      checked={allSel}
                      indeterminate={someSel && !allSel}
                      onChange={() => {
                        setSelectedRows(prev => {
                          const next = new Set(prev);
                          ids.forEach(id => (allSel ? next.delete(id) : next.add(id)));
                          return next;
                        });
                      }}
                    />
                  );
                })()}
              </TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    {accounts.length === 0
                      ? 'No Plaid-linked bank or card accounts — link one under Accounts.'
                      : 'No transactions match the current filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.slice(0, 1000).map(t => {
                const amt = Number(t.amount) || 0;
                const isOut = amt >= 0;
                const c = effCat(t);
                return (
                  <TableRow key={t.id} hover selected={t.rowId ? selectedRows.has(t.rowId) : false} sx={{ opacity: t.pending ? 0.6 : 1 }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        disabled={!t.rowId}
                        checked={t.rowId ? selectedRows.has(t.rowId) : false}
                        onChange={() => toggleRowSelected(t.rowId)}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {t.date}
                      {t.pending && <Chip label="pending" size="small" sx={{ ml: 0.75, height: 16, fontSize: '0.62rem' }} />}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.accountName}</TableCell>
                    <TableCell sx={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.name}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t.rowId ? 'Click to edit category' : ''}>
                        <Chip
                          size="small"
                          variant={c.source === 'plaid' ? 'outlined' : 'filled'}
                          color={c.source === 'override' ? 'primary' : (c.source === 'rule' ? 'secondary' : 'default')}
                          label={humanizeCategory(c.sub || c.primary)}
                          title={
                            (c.sub ? `${humanizeCategory(c.primary)} › ` : '') +
                            (c.source === 'override' ? `Override: ${t.categoryOverride}`
                              : c.source === 'rule' ? 'Auto rule (merchant match)'
                              : (t.categoryDetailed || t.categoryPrimary || ''))
                          }
                          onClick={() => openCatDialog(t)}
                          onDelete={t.rowId ? () => openCatDialog(t) : undefined}
                          deleteIcon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />}
                          sx={{ height: 22, fontSize: '0.7rem', maxWidth: 240, cursor: t.rowId ? 'pointer' : 'default' }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      color: isOut ? 'error.main' : 'success.main',
                    }}>
                      {isOut ? '-' : '+'}{formatCurrency(Math.abs(amt))}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {filtered.length > 1000 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Showing first 1,000 of {filtered.length.toLocaleString()} matching transactions — narrow the filters or date range.
            </Typography>
          </Box>
        )}
      </TableContainer>

      <Dialog open={!!catDialog} onClose={() => setCatDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Edit category</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 2, mt: 0.5 }}>
            {catDialog?.txn?.name}
          </Typography>
          <Autocomplete
            freeSolo
            autoFocus
            size="small"
            options={primaryOptions}
            inputValue={catDialog?.primary ?? ''}
            onInputChange={(_, v) => setCatDialog(d => (d ? { ...d, primary: v } : d))}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField {...params} label="Category" placeholder="e.g. Income" />
            )}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={subOptionsFor(catDialog?.primary || '')}
            inputValue={catDialog?.sub ?? ''}
            onInputChange={(_, v) => setCatDialog(d => (d ? { ...d, sub: v } : d))}
            sx={{ mb: 1 }}
            renderInput={(params) => (
              <TextField {...params} label="Subcategory" placeholder="Optional — e.g. Salary" />
            )}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                checked={!!catDialog?.applyFuture}
                onChange={e => setCatDialog(d => (d ? { ...d, applyFuture: e.target.checked } : d))}
              />
            }
            label={
              <Typography variant="body2">
                Also apply to future transactions matching “{catDialog?.txn?.name}”
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onClick={resetCategory}
            disabled={!catDialog?.txn?.categoryOverride}
            sx={{ mr: 'auto' }}
          >
            Reset to auto
          </Button>
          <Button size="small" onClick={() => setCatDialog(null)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            disabled={!String(catDialog?.primary || '').trim()}
            onClick={saveCatDialog}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!bulkDialog} onClose={() => setBulkDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Apply category to {selectedRows.size} transaction{selectedRows.size === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            autoFocus
            size="small"
            options={primaryOptions}
            inputValue={bulkDialog?.primary ?? ''}
            onInputChange={(_, v) => setBulkDialog(d => (d ? { ...d, primary: v } : d))}
            sx={{ mb: 2, mt: 0.5 }}
            renderInput={(params) => (
              <TextField {...params} label="Category" placeholder="e.g. Income" />
            )}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={subOptionsFor(bulkDialog?.primary || '')}
            inputValue={bulkDialog?.sub ?? ''}
            onInputChange={(_, v) => setBulkDialog(d => (d ? { ...d, sub: v } : d))}
            sx={{ mb: 1 }}
            renderInput={(params) => (
              <TextField {...params} label="Subcategory" placeholder="Optional — e.g. Salary" />
            )}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                checked={!!bulkDialog?.applyFuture}
                onChange={e => setBulkDialog(d => (d ? { ...d, applyFuture: e.target.checked } : d))}
              />
            }
            label={
              <Typography variant="body2">
                Also apply to future transactions matching these descriptions
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setBulkDialog(null)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            disabled={!String(bulkDialog?.primary || '').trim()}
            onClick={saveBulkCategory}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageCats} onClose={() => setManageCats(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Manage categories</DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          {taxonomy.length === 0 && (
            <Typography variant="body2" color="text.secondary">No categories yet.</Typography>
          )}
          {taxonomy.map(({ primary, subs }) => {
            const np = norm(primary);
            const primaryRenaming = renaming && norm(renaming.primary) === np && !renaming.sub;
            return (
              <Box key={np} sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {primaryRenaming ? (
                    <TextField
                      size="small"
                      autoFocus
                      fullWidth
                      value={renameVal}
                      onChange={e => setRenameVal(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRename();
                        if (e.key === 'Escape') setRenaming(null);
                      }}
                    />
                  ) : (
                    <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                      {humanizeCategory(primary)}
                    </Typography>
                  )}
                  {isCustomLeaf(primary, null) ? (
                    <>
                      <IconButton size="small" onClick={() => { setRenaming({ primary, sub: null }); setRenameVal(humanizeCategory(primary)); }}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" onClick={() => deleteLeaf(primary, null)}>
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.disabled">auto</Typography>
                  )}
                </Stack>
                {subs.filter(Boolean).map(sub => {
                  const subRenaming = renaming && norm(renaming.primary) === np && norm(renaming.sub || '') === norm(sub);
                  return (
                    <Stack key={norm(sub)} direction="row" alignItems="center" spacing={0.5} sx={{ pl: 3 }}>
                      {subRenaming ? (
                        <TextField
                          size="small"
                          autoFocus
                          fullWidth
                          value={renameVal}
                          onChange={e => setRenameVal(e.target.value)}
                          onBlur={commitRename}
                          onKeyDown={e => {
                            if (e.key === 'Enter') commitRename();
                            if (e.key === 'Escape') setRenaming(null);
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          {humanizeCategory(sub)}
                        </Typography>
                      )}
                      {isCustomLeaf(primary, sub) ? (
                        <>
                          <IconButton size="small" onClick={() => { setRenaming({ primary, sub }); setRenameVal(humanizeCategory(sub)); }}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton size="small" onClick={() => deleteLeaf(primary, sub)}>
                            <DeleteOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled">auto</Typography>
                      )}
                    </Stack>
                  );
                })}
                {addingSubFor === np ? (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pl: 3, mt: 0.5 }}>
                    <TextField
                      size="small"
                      autoFocus
                      fullWidth
                      placeholder="New subcategory"
                      value={newSubVal}
                      onChange={e => setNewSubVal(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newSubVal.trim()) {
                          registerCustomCat(primary, newSubVal.trim());
                          setNewSubVal('');
                          setAddingSubFor(null);
                        }
                        if (e.key === 'Escape') setAddingSubFor(null);
                      }}
                    />
                    <Button
                      size="small"
                      disabled={!newSubVal.trim()}
                      onClick={() => {
                        registerCustomCat(primary, newSubVal.trim());
                        setNewSubVal('');
                        setAddingSubFor(null);
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    sx={{ ml: 2.5, mt: 0.25, textTransform: 'none' }}
                    onClick={() => { setAddingSubFor(np); setNewSubVal(''); }}
                  >
                    + Subcategory
                  </Button>
                )}
              </Box>
            );
          })}
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              placeholder="New top-level category"
              value={newCatVal}
              onChange={e => setNewCatVal(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newCatVal.trim()) {
                  registerCustomCat(newCatVal.trim(), null);
                  setNewCatVal('');
                }
              }}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={!newCatVal.trim()}
              onClick={() => {
                registerCustomCat(newCatVal.trim(), null);
                setNewCatVal('');
              }}
            >
              Add
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            “auto” categories come from Plaid and can’t be renamed or removed. Removing a
            custom category resets the transactions using it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setManageCats(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Transactions;
